import { MessageCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * `BrandLogo` — the iCGChat brand mark: the green-gradient rounded tile with
 * the chat glyph, optionally followed by the "iCGChat" wordmark.
 *
 * Extracted so the exact same mark can render in TWO places without the
 * gradient-tile markup being copy-pasted (and drifting) between them:
 *
 *   - `AccountSidebar`'s top brand strip (the original home of this markup),
 *     at full `'default'` size.
 *   - `ConversationColumn`'s header (`'sm'`), so the console keeps its
 *     iCGChat branding even when the account rail is collapsed to zero width
 *     (see Layout's collapsible first panel). When the rail IS shown both
 *     places render the mark — that duplication is intentional, not a bug.
 *
 * Purely presentational: no state, no store, no props beyond size / wordmark
 * visibility / a className passthrough for the flex parent (the sidebar hands
 * it `flex-1` so the wordmark stretches and pushes the add-account button to
 * the header's right edge).
 */
interface BrandLogoProps {
  /**
   * `'default'` reproduces the account sidebar's original mark exactly
   * (size-9 tile / text-lg wordmark). `'sm'` is a slightly smaller variant
   * for the conversation header so the mark + wordmark + section title all
   * fit on one row without crowding.
   */
  size?: 'default' | 'sm';
  /** Render the "iCGChat" wordmark after the tile. Defaults to `true`. */
  showWordmark?: boolean;
  /** Extra classes on the outer flex wrapper. */
  className?: string;
}

const SIZES = {
  default: {
    wrap: 'gap-3',
    tile: 'size-9 rounded-xl',
    icon: 'size-5',
    word: 'text-lg',
  },
  sm: {
    wrap: 'gap-2',
    tile: 'size-8 rounded-[10px]',
    icon: 'size-[18px]',
    word: 'text-[15px]',
  },
} as const;

export function BrandLogo({
  size = 'default',
  showWordmark = true,
  className,
}: BrandLogoProps): React.JSX.Element {
  const s = SIZES[size];
  return (
    <div className={cn('flex items-center', s.wrap, className)}>
      {/* Gradient tile — `neu-gradient` + `shadow-neu-flat` are the same
          tokens the sidebar's original brand tile used. */}
      <div
        className={cn(
          'neu-gradient grid shrink-0 place-items-center shadow-neu-flat',
          s.tile,
        )}
      >
        <MessageCircle className={cn('text-white', s.icon)} aria-hidden="true" />
      </div>
      {showWordmark && (
        <span className={cn('font-extrabold tracking-tight', s.word)}>iCGChat</span>
      )}
    </div>
  );
}
